import type { Question, QuestionType, SurveyContent } from './contract'
import type { ResolvedMpid } from './mpid'
import { resolveMpid } from './mpid'
import { answerFormatValid, deserializeDateAnswer, serializeDateAnswer } from './question-precision'
import { resolveSelectionLimit, selectionCap, storedAnswerFits, submitField, violatesSelectionLimit } from './question-type'
import { textAnswerFormatInvalid } from './state'

// 一筆已保存的 demographic 答案，等同 SurveyResponse 去掉 question_id。
export interface StoredAnswer {
  type: QuestionType
  selected?: number[]
  text?: string
  date?: string
}

// 讀者已經填在畫面上的答案，兩張以題目 id 為 key 的 map（元件的 optionAnswers / textAnswers）。
export interface Answers {
  optionAnswers: Record<string, string[]>
  textAnswers: Record<string, string>
}

// 純函式：把已保存答案套用到當前問卷，只取 demographic 題，並逐項驗證（型別相符、
// 選項 seq 仍存在、選取數量限制、date 格式）。回傳可直接灌入元件狀態的兩個 map。
export function resolvePrefill(
  survey: Pick<SurveyContent, 'questions'>,
  stored: Record<string, StoredAnswer>,
): Answers {
  const optionAnswers: Record<string, string[]> = {}
  const textAnswers: Record<string, string> = {}
  for (const q of survey.questions) {
    if (q.category !== 'demographic')
      continue
    const s = stored[q.id]
    if (!s || !storedAnswerFits(s.type, q.type))
      continue
    const field = submitField(q.type)
    if (field !== undefined) {
      const raw = field === 'date' ? s.date : s.text
      // Guard against tampered/malformed stored data: a non-string would throw on .trim().
      if (typeof raw !== 'string')
        continue
      const value = raw.trim()
      // 格式一律照該題型的規則重驗，不只 date：存下來的答案可能是竄改過的 localStorage，
      // 也可能是題型改過之後的舊值。沒有格式規則的題型（text）這一關恆過。
      //
      // 這一關用**題型**的規則：存下來的一律是 wire 形狀（完整日期），只問年份的題目也
      // 不例外。畫面上的四位數是 deserialize 之後才有的事。
      if (value.length > 0 && !textAnswerFormatInvalid(q.type, value)) {
        const seeded = deserializeDateAnswer(q, value)
        // 種進去的值還得通得過 answerGap，否則一題選填題會把整頁推進鎖死：原生日期選單
        // 沒有上下限，讀者以前選得出 2030-06-01 這種值 —— 它過得了題型的 date 規則，卻
        // 落在年份題的區間外。兩道關卡問的是不同的事（wire 形狀 vs 這一題收不收），少了
        // 第二道，「seed 進去的值必然通得過閘門」這個不變式就破了。種不進去就當沒記憶。
        if (answerFormatValid(q, seeded))
          textAnswers[q.id] = seeded
      }
      continue
    }
    // A non-array selected (tampered storage) would throw on .filter(); treat it as no seed.
    if (!Array.isArray(s.selected))
      continue
    const validSeqs = new Set(q.options?.map(o => o.seq) ?? [])
    // 去重：竄改過的 storage 可能帶重複 seq，留著會讓上限少算、送出重複的選項。
    const picked = [...new Set(s.selected.filter(seq => validSeqs.has(seq)))]
    const limit = resolveSelectionLimit(q)
    const cap = selectionCap(limit)
    const capped = cap !== null ? picked.slice(0, cap) : picked
    // 截完仍不符限制（at_least／exactly 存下的勾選數不足）就不種：種進去的值要通得過
    // answerGap，否則一題選填題會在讀者沒動過的情況下擋住推進（同上面文字題那道第二關）。
    if (violatesSelectionLimit(limit, new Set(capped).size))
      continue
    if (capped.length > 0)
      optionAnswers[q.id] = capped.map(String)
  }
  return { optionAnswers, textAnswers }
}

const KEY_PREFIX = 'pulse.prefill.'

interface PrefillStore { v: 1, answers: Record<string, StoredAnswer> }

function isEmptyAnswer(a: StoredAnswer): boolean {
  if (a.selected !== undefined)
    return a.selected.length === 0
  if (a.text !== undefined)
    return a.text.trim().length === 0
  if (a.date !== undefined)
    return a.date.trim().length === 0
  return true
}

// 讀取某 mpid 的已保存答案 map。空 mpid（匿名）、不存在、壞 JSON 或 storage 讀取拋錯一律回 {}。
// 不逐筆深驗；資料正確性交由 resolvePrefill 對照當前問卷把關。
export function readPrefill(mpid: string): Record<string, StoredAnswer> {
  if (!mpid)
    return {}
  try {
    const raw = localStorage.getItem(KEY_PREFIX + mpid)
    if (!raw)
      return {}
    const parsed = JSON.parse(raw) as unknown
    const answers = (parsed as { answers?: unknown } | null)?.answers
    // Array.isArray: typeof [] is 'object' too, and writing a string key onto an array would be
    // dropped by JSON.stringify — every later save would silently vanish.
    if (!answers || typeof answers !== 'object' || Array.isArray(answers))
      return {}
    return answers as Record<string, StoredAnswer>
  }
  catch {
    return {}
  }
}

// 寫入/刪除某 mpid 的單題答案。空 mpid（匿名）不寫；answer 為 null 或空答案 → 刪除該題 entry；
// map 清空後移除整個 key。任何 storage 失敗（配額/無痕/停用）靜默 no-op，不崩宿主頁。
export function savePrefillAnswer(mpid: string, questionId: string, answer: StoredAnswer | null): void {
  if (!mpid)
    return
  try {
    const answers = readPrefill(mpid)
    if (answer === null || isEmptyAnswer(answer)) {
      if (!(questionId in answers))
        return
      delete answers[questionId]
    }
    else {
      answers[questionId] = answer
    }
    const key = KEY_PREFIX + mpid
    if (Object.keys(answers).length === 0)
      localStorage.removeItem(key)
    else
      localStorage.setItem(key, JSON.stringify({ v: 1, answers } satisfies PrefillStore))
  }
  catch {
    // storage unavailable — degrade to no prefill.
  }
}

/**
 * 一次載入期間，「這位讀者是誰、他先前告訴過我們什麼」。
 *
 * 存在的理由是三件事的順序：身分要在讀記憶**之前**解析、解析出來的 id 要一路用到送出、
 * 而記憶只在讀者有身分時才寫得進去。順序寫錯沒有任何東西會叫——最貴的那種寫錯（GET 與 POST
 * 帶不同 mp_id）連畫面都正常，只有後端資料裡多一筆對不起來的作答。
 *
 * 收進這裡之後，這條路只剩一個入口：身分在 `openPrefill` 解析一次，記憶只透過它回傳的物件讀寫。
 * 這是慣例，不是強制邊界——`resolveMpid` 與下面三支 primitive 都還是 export 的（各自有測試），
 * 所以第二個入口寫得出來、也編得過。`rg 'resolveMpid|readPrefill\(|savePrefillAnswer\(' src/`
 * 看得出當下有幾個。
 */
export interface PrefillSession {
  /** 這次載入的讀者身分。GET 的 mp_id、記憶的 storage key 與送出的 mp_id 都讀這一份。 */
  readonly mpid: ResolvedMpid
  /** 把記得的答案套到這份問卷上，與讀者已經填的合併。已經填的贏——重載不得抹掉填到一半的答案。 */
  seed: (survey: Pick<SurveyContent, 'questions'>, answered: Answers) => Answers
  /** 記住（或忘掉）一題的答案。非 demographic 題、匿名讀者一律 no-op。 */
  remember: (q: Question, answered: Answers) => void
}

// 開場即定身分。preview 不開 session（property-fed、沒有讀者），所以「預覽不寫記憶」不是一條
// 要記得檢查的規則，而是沒有東西可以寫。
export function openPrefill(explicitMpid: string): PrefillSession {
  const mpid = resolveMpid(explicitMpid)
  return {
    mpid,
    seed(survey, answered) {
      const stored = resolvePrefill(survey, readPrefill(mpid.id))
      return {
        optionAnswers: { ...stored.optionAnswers, ...answered.optionAnswers },
        textAnswers: { ...stored.textAnswers, ...answered.textAnswers },
      }
    },
    remember(q, answered) {
      // 題庫題（demographic）才跨問卷重用，其餘是該問卷專屬的答案，記下來也沒有第二個地方用得到。
      if (q.category !== 'demographic')
        return
      const qId = String(q.id)
      const field = submitField(q.type)
      if (field !== undefined) {
        const value = (answered.textAnswers[qId] ?? '').trim()
        // 打到一半的值不覆蓋已經記住的答案。年份題是逐鍵寫入的：讀者從 1990 退成 199 的
        // 那一刻若照存，序列化出來的 199-01-01 下次讀回來會過不了格式重驗而整筆被丟掉 ——
        // 記住的出生年就這樣沒了，而且不會有任何徵狀。原生日期選單只吐得出完整日期或空
        // 字串，所以這條路是窄輸入框開出來的；email／phone 打到一半也是同一回事。
        // 清空仍然是清空（value 為空走下面的 null），只有「非空但不合法」才留著舊記憶。
        if (value.length > 0 && !answerFormatValid(q, value))
          return
        // 兩個欄位問的是不同的事，刻意不共用一個判斷：`type` 是題目自己的型別，resolvePrefill
        // 讀回來時拿它跟當前題目對帳；值落在 date 還是 text 則跟著送出映射走。一題 email 因此
        // 存成 { type: 'email', text }。把 type 也換成映射結果，下次就對不上而整筆被丟掉。
        const answer: StoredAnswer | null = value
          ? (field === 'date' ? { type: q.type, date: serializeDateAnswer(q, value) } : { type: q.type, text: value })
          : null
        savePrefillAnswer(mpid.id, qId, answer)
        return
      }
      const selected = (answered.optionAnswers[qId] ?? []).map(Number)
      savePrefillAnswer(mpid.id, qId, selected.length > 0 ? { type: q.type, selected } : null)
    },
  }
}
